import React, { useEffect, useMemo, useState } from 'react';
import { masterAdminApi } from '../services/api';

function formatDateTime(value) {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleString();
}

function formatUptime(seconds) {
    if (!Number.isFinite(Number(seconds))) return '-';
    const total = Number(seconds);
    const days = Math.floor(total / 86400);
    const hours = Math.floor((total % 86400) / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
}

function labelStatus(status) {
    return String(status || '-').replace(/_/g, ' ');
}

function HealthDot({ ok, label }) {
    return (
        <span className={`monitor-check ${ok ? 'ok' : 'bad'}`}>
            <span />
            {label}
        </span>
    );
}

export default function MasterMonitorPage() {
    const [rows, setRows] = useState([]);
    const [summary, setSummary] = useState(null);
    const [checkedAt, setCheckedAt] = useState(null);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState('all');
    const [error, setError] = useState('');

    const filteredRows = useMemo(() => {
        if (statusFilter === 'all') return rows;
        return rows.filter((row) => row.monitorStatus === statusFilter);
    }, [rows, statusFilter]);

    const problemRows = useMemo(
        () => rows.filter((row) => row.monitorStatus !== 'online'),
        [rows]
    );

    const loadMonitor = async () => {
        setLoading(true);
        setError('');
        const response = await masterAdminApi.monitorStores({ limit: 200, timeoutMs: 6000 });
        if (!response.success) {
            setRows([]);
            setSummary(null);
            setError(response.error || 'Could not load server monitor.');
            setLoading(false);
            return;
        }

        setRows(Array.isArray(response.data) ? response.data : []);
        setSummary(response.summary || null);
        setCheckedAt(response.checkedAt || new Date().toISOString());
        setLoading(false);
    };

    useEffect(() => {
        loadMonitor();
    }, []);

    return (
        <div className="monitor-page">
            <div className="monitor-header">
                <div>
                    <p className="monitor-kicker">Master Operations</p>
                    <h2>Store Server Monitor</h2>
                    <p>Live status across store servers, application runtime, database, licence, uptime, and response speed.</p>
                </div>
                <div className="monitor-actions">
                    <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
                        <option value="all">All statuses</option>
                        <option value="online">Online</option>
                        <option value="degraded">Degraded</option>
                        <option value="offline">Offline</option>
                        <option value="not_configured">Not configured</option>
                    </select>
                    <button className="btn btn-dark" type="button" onClick={loadMonitor} disabled={loading}>
                        {loading ? 'Checking...' : 'Refresh'}
                    </button>
                </div>
            </div>

            <div className="monitor-summary">
                <button type="button" className="monitor-summary-tile" onClick={() => setStatusFilter('all')}>
                    <span>Total Stores</span>
                    <strong>{summary?.total ?? 0}</strong>
                    <small>{formatDateTime(checkedAt)}</small>
                </button>
                <button type="button" className="monitor-summary-tile online" onClick={() => setStatusFilter('online')}>
                    <span>Online</span>
                    <strong>{summary?.online ?? 0}</strong>
                    <small>Healthy app, DB, and licence</small>
                </button>
                <button type="button" className="monitor-summary-tile degraded" onClick={() => setStatusFilter('degraded')}>
                    <span>Degraded</span>
                    <strong>{summary?.degraded ?? 0}</strong>
                    <small>Responding with a failed check</small>
                </button>
                <button type="button" className="monitor-summary-tile offline" onClick={() => setStatusFilter('offline')}>
                    <span>Offline</span>
                    <strong>{summary?.offline ?? 0}</strong>
                    <small>Not reachable from master</small>
                </button>
                <button type="button" className="monitor-summary-tile muted" onClick={() => setStatusFilter('not_configured')}>
                    <span>No URL</span>
                    <strong>{summary?.not_configured ?? 0}</strong>
                    <small>Add store server URL</small>
                </button>
            </div>

            {error ? <div className="monitor-alert">{error}</div> : null}

            {problemRows.length > 0 ? (
                <div className="monitor-strip">
                    <strong>{problemRows.length} store{problemRows.length === 1 ? '' : 's'} need attention</strong>
                    <span>{problemRows.slice(0, 3).map((row) => row.tenantName).join(', ')}{problemRows.length > 3 ? '...' : ''}</span>
                </div>
            ) : null}

            <div className="monitor-grid">
                {loading ? (
                    <div className="monitor-empty">Checking store servers...</div>
                ) : filteredRows.length === 0 ? (
                    <div className="monitor-empty">No store server records match this view.</div>
                ) : filteredRows.map((row) => (
                    <article key={row.tenantId} className={`monitor-store ${row.monitorStatus}`}>
                        <div className="monitor-store-top">
                            <div>
                                <span className={`monitor-state ${row.monitorStatus}`}>{labelStatus(row.monitorStatus)}</span>
                                <h3>{row.tenantName}</h3>
                                <p>{row.tenantEmail || 'No contact email'}</p>
                            </div>
                            <div className="monitor-latency">
                                <strong>{row.latencyMs !== null && row.latencyMs !== undefined ? `${row.latencyMs}` : '-'}</strong>
                                <span>ms</span>
                            </div>
                        </div>

                        <div className="monitor-server-url">
                            {row.serverUrl ? (
                                <a href={row.serverUrl} target="_blank" rel="noreferrer">{row.serverUrl}</a>
                            ) : (
                                <span>No server URL configured</span>
                            )}
                        </div>

                        <div className="monitor-checks">
                            <HealthDot ok={row.applicationRunning} label="App" />
                            <HealthDot ok={row.deploymentRoleOk !== false} label="Store Role" />
                            <HealthDot ok={row.storeAdminRolesOk !== false && row.adminRolesOk !== false} label="Admin Roles" />
                            <HealthDot ok={row.databaseOk} label="Database" />
                            <HealthDot ok={row.licenceOk} label="Licence" />
                        </div>

                        <div className="monitor-meta">
                            <div>
                                <span>Uptime</span>
                                <strong>{formatUptime(row.uptimeSeconds)}</strong>
                            </div>
                            <div>
                                <span>Plan</span>
                                <strong>{row.licence?.plan || row.subscriptionPlan || '-'}</strong>
                            </div>
                            <div>
                                <span>Licence</span>
                                <strong>{row.licence?.daysRemaining !== undefined && row.licence?.daysRemaining !== null ? `${row.licence.daysRemaining}d` : '-'}</strong>
                            </div>
                        </div>

                        <div className="monitor-message">
                            <span>{row.message || '-'}</span>
                            <small>Checked {formatDateTime(row.checkedAt)}</small>
                        </div>
                    </article>
                ))}
            </div>
        </div>
    );
}
